'use client'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import ArrowCircleButton from './ArrowInCircleButton'
import ImageCaptionInfo from './ImageCaptionInfo'

function ZoomSlider({ pictures, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? pictures.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === pictures.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  const currentPicture = pictures[currentIndex].picture;
  const legend = pictures[currentIndex].legend;

  return (
    <div className="fixed inset-0 z-[999] h-screen w-screen bg-white flex flex-col">
      <div className="flex justify-between items-center p-8 pt-24 large:pt-8 w-full">
        <span className="font-sans text-sm">
          {currentIndex + 1} / {pictures.length}
        </span>
        <button className="text-2xl font-bold" onClick={onClose}>✕</button>
      </div>

      <div className="relative flex flex-1 items-center justify-center px-0 lg:px-4">
        <button
          className="absolute left-8 bottom-0 large:bottom-auto z-10 hover:scale-110 transition-transform rounded-full bg-white"
          onClick={goToPrevious}
        >
          <ArrowCircleButton rotate color={'black'} />
        </button>

        <div className="w-full max-w-5xl h-full relative flex items-center justify-center group">
          <div className="relative">
            <Image
              className="rounded object-contain max-h-[80vh]"
              src={currentPicture.url}
              width={currentPicture.width}
              height={currentPicture.height}
              alt={currentPicture.alternativeText ?? ''}
            />

            {/* Info Button - appears on hover */}
            <ImageCaptionInfo caption={currentPicture.caption} legend={legend} />
            </div>
        </div>

        <button
          className="absolute right-8 bottom-0 large:bottom-auto z-10 hover:scale-110 transition-transform rounded-full bg-white"
          onClick={goToNext}
        >
          <ArrowCircleButton color={'black'} />
        </button>
      </div>

      <p className="text-center p-8 italic">
        {currentPicture.alternativeText}
      </p>
    </div>
  );
}

export default function ImagesGallery({ pictures, videos }) {
  const t = useTranslations('ImagesGallery')

  const containerRef = useRef<HTMLDivElement | null>(null)

  const [zoomIndex, setZoomIndex] = useState<number | null>(null)

  const scroll = (offset: number) => {
    const el = containerRef.current
    if (!el) return
    el.scrollBy({ left: offset, behavior: 'smooth' })
  }

  return (
    <>
      {pictures?.length > 0 && (
        <section className="flex w-full flex-col items-start justify-start gap-8">
          <div className="flex w-full flex-row flex-wrap justify-between gap-8">
            <h2
              className="font-playfairDisplay-bold-italic text-3xl"
            >
              <span>{t('picturesGallery')}</span>

            </h2>

            <div className="z-[50] flex flex-row hidden large:flex">
              <button onClick={() => scroll(-400)}>
                <ArrowCircleButton rotate color={'black'} />
              </button>

              <button onClick={() => scroll(400)}>
                <ArrowCircleButton color={'black'} />
              </button>
            </div>

            <div ref={containerRef} className="h-full w-full overflow-auto">
              <ul className="flex w-fit justify-center gap-4">
                {pictures.map((p, index) => (
                  <li
                    key={p.id}
                    className="relative overflow-hidden rounded text-center"
                    style={{ width: typeof window !== 'undefined' ? window.innerWidth < 1024 ? window.innerWidth - 100 : 400 : undefined }}
                  >
                    <Image
                      className="aspect-video w-full cursor-pointer rounded object-cover hover:opacity-90 transition-opacity"
                      src={p.picture.url}
                      width={p.picture.width / 2}
                      height={p.picture.height / 2}
                      alt={p.picture.alternativeText ?? ''}
                      onClick={() => setZoomIndex(index)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Zoom Slider Modal */}
      {zoomIndex !== null && (
        <ZoomSlider
          pictures={pictures}
          initialIndex={zoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}

      {videos?.length > 0 && (
        <section className="flex flex-col gap-8 p-8">
          <h2 className="font-playfairDisplay-bold-italic text-3xl">
            {t('videoGallery')}
          </h2>

          <div>
            {videos.map((v) => (
              <video
                controls
                width={v.video.width}
                height={v.video.height}
                key={v.id}
              >
                <source src={v.video.url} type={v.video.mime} />
              </video>
            ))}
          </div>
        </section>
      )}
    </>
  )
}
